<template>
  <div class="page-wrapper">
    <div class="content container-fluid">
      <!-- HEADER -->
      <div class="page-header d-flex justify-content-between align-items-center">
        <div>
          <h3 class="page-title mb-1">
            <i class="fa fa-cube mr-2 text-primary"></i>
            Lịch sử Lấy hàng / Giao hàng (Admin)
          </h3>
          <div class="text-muted small">
            Xem tất cả xe, tất cả tài xế, có ảnh và ghi chú.
          </div>
        </div>

        <button class="btn btn-outline-secondary" @click="refresh" :disabled="loading">
          <i class="fa fa-refresh mr-1"></i> Làm mới
        </button>
      </div>

      <!-- FILTER -->
      <div class="card mb-3">
        <div class="card-body">
          <div class="row">
            <div class="col-md-4 mb-2">
              <label class="small text-muted font-weight-bold">Tìm nhanh</label>
              <input
                v-model.trim="q"
                class="form-control"
                placeholder="Xe / điểm / tài xế..."
                @keyup.enter="applyFilters"
              />
            </div>

            <div class="col-md-2 col-6 mb-2">
              <label class="small text-muted font-weight-bold">Loại</label>
              <select v-model="jobType" class="form-control">
                <option value="">Tất cả</option>
                <option value="PICKUP">Lấy hàng</option>
                <option value="DELIVERY">Giao hàng</option>
              </select>
            </div>

            <div class="col-md-2 col-6 mb-2">
              <label class="small text-muted font-weight-bold">Chế độ</label>
              <select v-model="jobMode" class="form-control">
                <option value="">Tất cả</option>
                <option value="NORMAL">Bình thường</option>
                <option value="DIRECT_DELIVERY">Hàng sẵn</option>
              </select>
            </div>

            <div class="col-md-2 col-6 mb-2">
              <label class="small text-muted font-weight-bold">Từ ngày</label>
              <input v-model="from" type="date" class="form-control" />
            </div>

            <div class="col-md-2 col-6 mb-2">
              <label class="small text-muted font-weight-bold">Đến ngày</label>
              <input v-model="to" type="date" class="form-control" />
            </div>
          </div>

          <div class="d-flex mt-2">
            <button class="btn btn-primary mr-2" @click="applyFilters" :disabled="loading">
              <i class="fa fa-filter mr-1"></i> Lọc
            </button>
            <button class="btn btn-outline-danger" @click="resetFilters" :disabled="loading">
              <i class="fa fa-undo mr-1"></i> Reset
            </button>

            <div class="ml-auto text-muted small d-flex align-items-center">
              Tổng: <b class="ml-1">{{ total }}</b>
            </div>
          </div>

          <div v-if="error" class="alert alert-danger py-2 mt-3 mb-0">
            <i class="fa fa-exclamation-triangle mr-1"></i> {{ error }}
          </div>
        </div>
      </div>

      <!-- TABLE -->
      <div class="card">
        <div class="card-body p-0">
          <div v-if="loading" class="p-4 text-center text-muted">
            <span class="spinner-border spinner-border-sm mr-2"></span> Đang tải...
          </div>

          <div v-else-if="!list.length" class="p-4 text-center text-muted">
            Không có dữ liệu.
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover mb-0">
              <thead class="thead-light">
                <tr>
                  <th>#</th>
                  <th>Thời gian tạo</th>
                  <th>Mã PC</th>
                  <th>Xe</th>
                  <th>Tài xế</th>
                  <th>Loại</th>
                  <th>Chế độ</th>
                  <th>Điểm</th>
                  <th>Bắt đầu</th>
                  <th>Kết thúc</th>
                  <th>Số giờ</th>
                  <th>Ảnh</th>
                  <th>Ghi chú</th>
                  <th class="text-right"></th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="item in list" :key="item.id">
                  <td class="text-muted">#{{ item.id }}</td>

                  <td>
                    <div class="font-weight-bold">{{ fmt(item.createdAt) }}</div>
                    <div class="small text-muted">{{ durationText(item.startTime, item.endTime) }}</div>
                  </td>

                  <td>
                    <span class="badge badge-light">
                      {{ item.assignmentId ? ("PC#" + item.assignmentId) : "-" }}
                    </span>
                  </td>

                  <td><b>#{{ item.xeId }}</b></td>

                  <td><b>{{ item.taiXeTen || ("#" + item.taiXeId) }}</b></td>

                  <td>
                    <span
                      class="badge"
                      :class="item.jobType === 'PICKUP' ? 'badge-success' : 'badge-warning'"
                    >
                      {{ item.jobType === 'PICKUP' ? 'Lấy hàng' : 'Giao hàng' }}
                    </span>
                  </td>

                  <td>
                    <span
                      class="badge"
                      :class="item.jobMode === 'DIRECT_DELIVERY' ? 'badge-info' : 'badge-secondary'"
                    >
                      {{ item.jobMode === 'DIRECT_DELIVERY' ? 'Hàng sẵn' : 'Bình thường' }}
                    </span>
                  </td>

                  <td class="cell-wrap">{{ item.locationName || "-" }}</td>

                  <td>{{ fmt(item.startTime) }}</td>
                  <td>{{ fmt(item.endTime) }}</td>

                  <td>
                    <span v-if="item.overtimeHours > 0" class="badge badge-danger">
                      +{{ item.overtimeHours }}h
                    </span>
                    <span v-else class="text-muted">0h</span>
                  </td>

                  <td>
                    <div v-if="item.photos?.length" class="thumb-row">
                      <img
                        v-for="(p,i) in item.photos.slice(0,3)"
                        :key="p+i"
                        :src="fixImg(p)"
                        class="thumb"
                        @click="openLightbox(item.photos.map(fixImg), i)"
                        :title="'Mở ảnh ' + (i+1)"
                      />
                      <a
                        v-if="item.photos.length > 3"
                        href="javascript:void(0)"
                        class="text-muted small ml-1"
                        @click="openLightbox(item.photos.map(fixImg), 0)"
                      >
                        +{{ item.photos.length - 3 }}
                      </a>
                    </div>
                    <span v-else class="text-muted">-</span>
                  </td>

                  <td class="cell-wrap">{{ item.note || "-" }}</td>

                  <td class="text-right">
                    <button class="btn btn-sm btn-outline-dark" @click="openDetail(item)">
                      <i class="fa fa-eye mr-1"></i> Xem
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- PAGINATION -->
          <div class="p-3 d-flex align-items-center">
            <div class="text-muted small">
              Trang {{ page }} / {{ totalPages }}
            </div>
            <div class="ml-auto btn-group">
              <button
                class="btn btn-outline-secondary"
                @click="prevPage"
                :disabled="page <= 1"
              >
                <i class="fa fa-angle-left"></i>
              </button>
              <button
                class="btn btn-outline-secondary"
                @click="nextPage"
                :disabled="page >= totalPages"
              >
                <i class="fa fa-angle-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- DETAIL MODAL -->
      <div v-if="detailOpen" class="modal-backdrop" @click.self="detailOpen=false">
        <div class="classic-dialog">
          <div class="dialog-header d-flex justify-content-between align-items-center">
            <div>
              <b>Chi tiết Job #{{ detail.id }}</b>
              <div class="small text-muted">
                {{ labelType(detail.jobType) }} • {{ labelMode(detail.jobMode) }}
              </div>
            </div>
            <button class="btn btn-sm btn-light" @click="detailOpen=false">&times;</button>
          </div>

          <div class="dialog-body">
            <div class="row">
              <div class="col-md-6 mb-2">
                <div class="kv"><span class="k">Xe</span><span class="v">#{{ detail.xeId }}</span></div>
                <div class="kv"><span class="k">Tài xế</span><span class="v">{{ detail.taiXeTen || ("#" + detail.taiXeId) }}</span></div>
                <div class="kv"><span class="k">Mã phân công</span><span class="v">{{ detail.assignmentId ? ("PC#" + detail.assignmentId) : "-" }}</span></div>
                <div class="kv"><span class="k">Điểm</span><span class="v">{{ detail.locationName || "-" }}</span></div>
              </div>

              <div class="col-md-6 mb-2">
                <div class="kv"><span class="k">Tạo lúc</span><span class="v">{{ fmt(detail.createdAt) }}</span></div>
                <div class="kv"><span class="k">Bắt đầu</span><span class="v">{{ fmt(detail.startTime) }}</span></div>
                <div class="kv"><span class="k">Kết thúc</span><span class="v">{{ fmt(detail.endTime) }}</span></div>
                <div class="kv">
                  <span class="k">Thời lượng</span>
                  <span class="v">{{ durationText(detail.startTime, detail.endTime) }}</span>
                </div>
                <div class="kv">
                  <span class="k">Tăng ca</span>
                  <span class="v">
                    <span v-if="detail.overtimeHours > 0" class="badge badge-danger">+{{ detail.overtimeHours }}h</span>
                    <span v-else class="text-muted">0h</span>
                  </span>
                </div>
              </div>
            </div>

            <div class="mt-2">
              <b>Ghi chú:</b>
              <div class="note-box mt-1">{{ detail.note || "-" }}</div>
            </div>

            <div class="mt-3">
              <div class="d-flex align-items-center justify-content-between">
                <b>Ảnh:</b>
                <a
                  v-if="detail.photos?.length"
                  class="small"
                  :href="fixImg(detail.photos[0])"
                  target="_blank"
                  rel="noopener"
                >
                  Mở ảnh đầu tiên <i class="fa fa-external-link ml-1"></i>
                </a>
              </div>

              <div v-if="detail.photos?.length" class="photo-grid mt-2">
                <img
                  v-for="(p,i) in detail.photos"
                  :key="p+i"
                  :src="fixImg(p)"
                  @click="openLightbox(detail.photos.map(fixImg), i)"
                />
              </div>
              <div v-else class="text-muted">Không có ảnh</div>
            </div>
          </div>

          <div class="dialog-footer">
            <button class="btn btn-dark btn-block" @click="detailOpen=false">Đóng</button>
          </div>
        </div>
      </div>

      <!-- LIGHTBOX -->
      <vue-easy-lightbox
        :visible="lightbox.visible"
        :imgs="lightbox.imgs"
        :index="lightbox.index"
        @hide="lightbox.visible=false"
      />
    </div>
  </div>
</template>

<script>
import { useAdminDriverJobStore } from "@/stores/adminDriverJobStore";
import VueEasyLightbox from "vue-easy-lightbox";

export default {
  name: "AdminGiaoNhanHangHistory",
  components: { VueEasyLightbox },

  data() {
    return {
      detailOpen: false,
      detail: {},
      lightbox: { visible: false, imgs: [], index: 0 },
    };
  },

  computed: {
    store() { return useAdminDriverJobStore(); },
    list() { return this.store.list; },
    total() { return this.store.total; },
    page() { return this.store.page; },
    size() { return this.store.size; },
    loading() { return this.store.loading; },
    error() { return this.store.error; },

    q: { get() { return this.store.q; }, set(v) { this.store.q = v; } },
    jobType: { get() { return this.store.jobType; }, set(v) { this.store.jobType = v; } },
    jobMode: { get() { return this.store.jobMode; }, set(v) { this.store.jobMode = v; } },
    from: { get() { return this.store.from; }, set(v) { this.store.from = v; } },
    to: { get() { return this.store.to; }, set(v) { this.store.to = v; } },

    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.size));
    },
  },

  methods: {
    refresh() { this.store.fetch(); },
    applyFilters() { this.store.page = 1; this.store.fetch(); },
    resetFilters() { this.store.resetFilters(); },
    prevPage() { if (this.page > 1) this.store.setPage(this.page - 1); },
    nextPage() { if (this.page < this.totalPages) this.store.setPage(this.page + 1); },

    fmt(s) {
      if (!s) return "-";
      const d = new Date(s);
      return isNaN(d) ? "-" : d.toLocaleString("vi-VN", { hour12: false });
    },

    labelType(t) {
      if (t === "PICKUP") return "Lấy hàng";
      if (t === "DELIVERY") return "Giao hàng";
      return t || "-";
    },

    labelMode(m) {
      if (m === "DIRECT_DELIVERY") return "Hàng sẵn";
      if (m === "NORMAL") return "Bình thường";
      return m || "-";
    },

    durationText(start, end) {
      if (!start || !end) return "-";
      const s = new Date(start).getTime();
      const e = new Date(end).getTime();
      if (isNaN(s) || isNaN(e) || e < s) return "-";
      const ms = e - s;
      const totalMin = Math.floor(ms / 60000);
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      if (h <= 0 && m <= 0) return "0 phút";
      if (h <= 0) return `${m} phút`;
      if (m === 0) return `${h} giờ`;
      return `${h} giờ ${m} phút`;
    },

    // Fix trường hợp API trả localhost khi FE chạy nơi khác (dev/prod)
    // Nếu bạn deploy cùng domain thì khỏi cần, nhưng đời đâu đẹp vậy.
    fixImg(url) {
      if (!url) return url;
      try {
        const u = new URL(url, window.location.origin);
        // Nếu là localhost mà FE không ở localhost, đổi sang API base nếu bạn có env
        const apiBase = process.env.VUE_APP_API_BASE_URL; // ví dụ: https://pm.thepminhtien.com
        if (apiBase && u.hostname === "localhost") {
          return apiBase.replace(/\/+$/, "") + u.pathname + (u.search || "");
        }
        return u.toString();
      } catch (e) {
        return url;
      }
    },

    openDetail(item) {
      this.detail = item || {};
      this.detailOpen = true;
    },

    openLightbox(imgs, index) {
      this.lightbox.imgs = imgs || [];
      this.lightbox.index = index || 0;
      this.lightbox.visible = true;
    },
  },

  mounted() {
    this.store.fetch();
  },
};
</script>

<style scoped>
.thumb-row { display:flex; gap:6px; align-items:center; }
.thumb{
  width:34px; height:34px; object-fit:cover;
  border-radius:6px; border:1px solid #e5e7eb;
  cursor:pointer;
}
.cell-wrap{
  max-width: 240px;
  white-space: normal;
  word-break: break-word;
}
.modal-backdrop{
  position:fixed; inset:0; background:rgba(0,0,0,.6);
  display:flex; align-items:center; justify-content:center; z-index:9999;
}
.classic-dialog{
  background:#fff; width:92%; max-width:820px; border-radius:12px;
  box-shadow: 0 20px 60px rgba(0,0,0,.25);
}
.dialog-header,.dialog-footer{
  padding:12px 14px; background:#f9fafb; border-bottom:1px solid #eee;
}
.dialog-footer{ border-bottom:0; border-top:1px solid #eee; }
.dialog-body{ padding:14px; max-height:70vh; overflow:auto; }

.photo-grid{ display:flex; flex-wrap:wrap; gap:8px; }
.photo-grid img{
  width:92px; height:92px; object-fit:cover;
  border-radius:10px; border:1px solid #e5e7eb;
  cursor:pointer;
}

.kv{ display:flex; justify-content:space-between; gap:12px; padding:4px 0; }
.k{ color:#6b7280; font-weight:600; }
.v{ color:#111827; text-align:right; }

.note-box{
  background:#f9fafb;
  border:1px solid #eee;
  border-radius:10px;
  padding:10px 12px;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
